import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Droplets, Download, CreditCard, RefreshCw, Wrench, AlertTriangle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import StaffLayout from '../../layouts/StaffLayout';
import { ordersApi } from '../../services/api';
import { formatCurrency, formatDateTime, StatusBadge, PaymentBadge, formatApiError } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getKanbanColumns } from '../../config/businessTypes';
import PaymentModal from '../../components/PaymentModal';
import DiagnosisModal from '../../components/DiagnosisModal';
import WaMessageModal from '../../components/WaMessageModal';

export default function StaffOrderDetailPage() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPayment, setShowPayment] = useState(false);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const { businessType } = useAuth();

  const STATUS_FLOW = getKanbanColumns(businessType).map(col => ({
    value: col.id,
    label: typeof col.label === 'object' ? (lang === 'id' ? col.label.id : col.label.en) : col.label,
  }));

  const { data: order, isLoading } = useQuery({
    queryKey: ['staff-order', id],
    queryFn: () => ordersApi.get(id).then(r => r.data),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => ordersApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-order', id] });
      toast.success(t('Status diperbarui', 'Status updated'));
    },
    onError: err => toast.error(formatApiError(err)),
  });

  if (isLoading) return (
    <StaffLayout>
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
      </div>
    </StaffLayout>
  );
  if (!order) return (
    <StaffLayout>
      <div className="text-center py-12 text-slate-400">{t('Pesanan tidak ditemukan', 'Order not found')}</div>
    </StaffLayout>
  );

  return (
    <StaffLayout>
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900 font-manrope font-mono">{order.tracking_code}</h1>
              <StatusBadge status={order.status} lang={lang} size="md" />
              <PaymentBadge status={order.payment_status} lang={lang} />
            </div>
            <p className="text-slate-500 text-sm mt-0.5">{t('Dibuat', 'Created')}: {formatDateTime(order.created_at)}</p>
          </div>
          {order.customer_phone && (
            <button onClick={() => setShowWa(true)} data-testid="open-wa-button"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 active:scale-95 transition-all shadow-sm">
              <MessageCircle size={16} />WA
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Diagnosis Warning Banner */}
          {order.form_type === 'servis' && order.total_amount === 0 && (
            <div className="lg:col-span-3 bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={18} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-orange-800">{t('Pesanan servis belum ada harga', 'Service order has no price yet')}</p>
                  <p className="text-xs text-orange-600 mt-0.5">{t('Input hasil diagnosa dan rincian biaya servis', 'Input diagnosis and service cost breakdown')}</p>
                </div>
              </div>
              <button onClick={() => setShowDiagnosis(true)} data-testid="open-diagnosis-button"
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 active:scale-95 transition-all flex-shrink-0">
                <Wrench size={15} />{t('Input Diagnosa & Harga', 'Input Diagnosis & Price')}
              </button>
            </div>
          )}

          {order.form_type === 'servis' && order.total_amount > 0 && (
            <div className="lg:col-span-3 bg-emerald-50 border border-emerald-100 rounded-2xl p-3.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Wrench size={15} className="text-[#0f6c44]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">{t('Pesanan Servis', 'Service Order')}</p>
                  {order.diagnosis_notes && <p className="text-xs text-emerald-600 mt-0.5 truncate max-w-sm">{order.diagnosis_notes}</p>}
                </div>
              </div>
              <button onClick={() => setShowDiagnosis(true)} data-testid="edit-diagnosis-button"
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-emerald-200 text-[#0f6c44] rounded-xl text-xs font-semibold hover:bg-emerald-50 transition-all flex-shrink-0">
                <Wrench size={13} />{t('Edit Diagnosa', 'Edit Diagnosis')}
              </button>
            </div>
          )}

          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Informasi Pesanan', 'Order Info')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">{t('Pelanggan', 'Customer')}</p>
                  <p className="font-semibold text-slate-800">{order.customer_name || '-'}</p>
                  {order.customer_phone && <p className="text-sm text-slate-500">{order.customer_phone}</p>}
                </div>
                {order.parfum && (
                  <div className="flex items-center gap-2">
                    <Droplets size={16} className="text-slate-400" />
                    <div><p className="text-xs text-slate-400">Parfum</p><p className="text-sm font-medium text-slate-700">{order.parfum}</p></div>
                  </div>
                )}
                {order.shelf_location && (
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-slate-400" />
                    <div><p className="text-xs text-slate-400">{t('Lokasi Rak', 'Shelf')}</p><p className="text-sm font-medium text-slate-700">{order.shelf_location}</p></div>
                  </div>
                )}
                {order.notes && <div className="col-span-2"><p className="text-xs text-slate-400 mb-0.5">{t('Catatan', 'Notes')}</p><p className="text-sm text-slate-700">{order.notes}</p></div>}
                {order.diagnosis_notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">{t('Catatan Diagnosa', 'Diagnosis Notes')}</p>
                    <p className="text-sm text-slate-700">{order.diagnosis_notes}</p>
                  </div>
                )}
              </div>

              {/* Foto Kondisi Barang */}
              {order.condition_photos && order.condition_photos.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-2 font-medium">{t('Foto Kondisi Barang', 'Condition Photos')}</p>
                  <div className="flex flex-wrap gap-2">
                    {order.condition_photos.map((path, idx) => (
                      <a key={idx} href={`/api/uploads/photo/${path}`} target="_blank" rel="noreferrer"
                        className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 block hover:opacity-80 transition-opacity">
                        <img src={`/api/uploads/photo/${path}`} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Item Pesanan', 'Order Items')}</h3>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    {['Item', t('Qty', 'Qty'), t('Harga', 'Price'), 'Subtotal'].map(h => (
                      <th key={h} className="text-left py-2 text-xs text-slate-400 font-semibold uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((item, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-3 text-sm font-medium text-slate-800">{item.product_name}</td>
                      <td className="py-3 text-sm text-slate-600">{item.quantity} {item.unit}</td>
                      <td className="py-3 text-sm text-slate-600">{formatCurrency(item.price)}</td>
                      <td className="py-3 text-sm font-semibold text-slate-800">{formatCurrency(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-100">
                    <td colSpan={3} className="py-3 text-sm font-bold text-slate-800">Total</td>
                    <td className="py-3 text-base font-extrabold text-[#0f6c44] font-manrope">{formatCurrency(order.total_amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 font-manrope">{t('Riwayat Pembayaran', 'Payment History')}</h3>
                {order.payment_status !== 'paid' && (
                  <button onClick={() => setShowPayment(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#0f6c44] text-white rounded-lg text-sm font-medium hover:bg-[#0c5636] transition-colors">
                    <CreditCard size={15} />{t('Catat Bayar', 'Add Payment')}
                  </button>
                )}
              </div>
              {(order.payments || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{t('Belum ada pembayaran', 'No payments yet')}</p>
              ) : (
                <div className="space-y-2">
                  {(order.payments || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{formatCurrency(p.amount)}</p>
                        <p className="text-xs text-slate-400">{p.payment_method?.toUpperCase()} · {formatDateTime(p.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Ringkasan Bayar', 'Payment Summary')}</h3>
              <div className="space-y-2.5">
                {[
                  { label: 'Total', value: order.total_amount },
                  { label: t('Terbayar', 'Paid'), value: order.paid_amount },
                ].map(r => (
                  <div key={r.label} className="flex justify-between">
                    <span className="text-sm text-slate-500">{r.label}</span>
                    <span className="text-sm font-semibold text-slate-800">{formatCurrency(r.value)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-slate-100">
                  <span className="text-sm font-bold text-slate-800">{t('Sisa', 'Remaining')}</span>
                  <span className={`text-sm font-bold ${order.remaining_amount > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatCurrency(order.remaining_amount)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Update Status', 'Update Status')}</h3>
              <div className="space-y-2">
                {STATUS_FLOW.map(s => (
                  <button key={s.value} onClick={() => statusMutation.mutate(s.value)}
                    disabled={order.status === s.value || statusMutation.isPending}
                    className={`w-full px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${order.status === s.value ? 'bg-[#0f6c44] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-3 font-manrope">{t('Download Nota', 'Download Receipt')}</h3>
              <div className="space-y-2">
                {['a4', 'a6', 'thermal'].map(fmt => (
                  <a key={fmt} href={ordersApi.getReceiptUrl(id, fmt)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-100 transition-colors">
                    <Download size={15} className="text-slate-400" />
                    {fmt.toUpperCase()} {fmt === 'thermal' ? '(80mm)' : ''}
                  </a>
                ))}
                <a href={`/track/${order.tracking_code}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 text-[#0f6c44] rounded-xl text-sm font-medium hover:bg-emerald-100 transition-colors">
                  <RefreshCw size={15} />
                  {t('Link Tracking', 'Tracking Link')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentModal order={order} onClose={() => setShowPayment(false)}
          onSuccess={() => { setShowPayment(false); queryClient.invalidateQueries({ queryKey: ['staff-order', id] }); }} />
      )}

      {showDiagnosis && (
        <DiagnosisModal order={order} onClose={() => setShowDiagnosis(false)}
          onSuccess={() => { setShowDiagnosis(false); queryClient.invalidateQueries({ queryKey: ['staff-order', id] }); }} />
      )}

      {showWa && (
        <WaMessageModal order={order} onClose={() => setShowWa(false)} onSent={() => {}} />
      )}
    </StaffLayout>
  );
}
